import React, { useCallback, useEffect, useState, useMemo } from "react";
import Header from "../../components/Lesson/Header";
import LessonList from "../../components/Lesson/LessonList";
import axios from "axios";
import Toast from "../../components/Sweetalert/Sweetalert";
import { API } from "../../shared/api/Api";
import { useLocation, useParams, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUserInformations } from "../../features/authSlice";
import { showToast } from "../../utils/utils";
import LessonTable from "../../components/Tables/LessonTable";
import { FiArrowLeft } from "react-icons/fi";

const LessonPage = () => {
  const params = useParams();
  const [lessons, setLessons] = useState([]);
  const [loader, setLoader] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [reFetchData, setReFetchData] = useState(false);
  const [orderBy, setOrderBy] = useState("created_at");
  const [orderDir, setOrderDir] = useState("DESC");
  const user = useSelector(selectUserInformations);
  const isInstructor = user.instructor ?? false;
  const isStudent = useMemo(() => user.student ?? false, [user.student]);

  const location = useLocation();
  
  // Determine back navigation path based on user role
  const backPath = useMemo(() => {
    if (isStudent) {
      // Students go back to their courses list
      return "/dashboard/courses";
    }
    // Admins/Instructors go back to course details
    return `/dashboard/courses/${params.id}`;
  }, [isStudent, params.id]);

  const fetchLessons = useCallback(async () => {
    setLoader(true);
    try {
      const { data } = await axios.get(
        `${API.Courses.app_courses_allLessonsForCourse}/${params.id}`,
        {
          params: {
            page: currentPage,
            search: search || undefined,
            orderBy: orderBy,
            orderDir: orderDir,
          },
          headers: { Authorization: `Bearer ${user.token}`, lang: "en" },
        }
      );

      if (data.status === 200 && data.errorCode === 0) {
        setLessons(data.data.items);
        setCurrentPage(data.data.current_page);
        setTotalPages(data.data.last_page);
      } else {
        showToast("Error", "Failed to fetch lessons", "error");
      }
    } catch (error) {
      showToast(
        "Error",
        error.response?.data?.message || "An error occurred",
        "error"
      );
    } finally {
      setLoader(false);
      setReFetchData(false);
    }
  }, [params.id, currentPage, search, user.token, orderBy, orderDir]);

  const handleSort = (column, direction) => {
    setOrderBy(column);
    setOrderDir(direction);
  };

  useEffect(() => {
    setLessons([]);
    fetchLessons();
  }, [reFetchData, fetchLessons]); // fetchLessons will cover its own dependencies

  const courseName = location.state?.course?.title || "Course";

  return (
    <div className="min-h-screen p-2 sl:pt-0 sm:p-2 md:p-4 sl:p-6">
      {/* Header with Back Navigation */}
      <div className="mb-4 flex items-center gap-4">
        <Link
          to={backPath}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700"
        >
          <FiArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {isStudent ? courseName : `Lessons for ${courseName}`}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {isStudent 
              ? "View course lessons and watch session recordings" 
              : "View and manage course lessons"
            }
          </p>
        </div>
      </div>

      <LessonTable
        currentPage={currentPage}
        lessons={lessons}
        course={location.state?.course}
        loader={loader}
        onPageChange={setCurrentPage}
        onSearchChange={setSearch}
        setReFetchData={setReFetchData}
        totalPages={totalPages}
        onSort={handleSort}
        currentOrderBy={orderBy}
        currentOrderDir={orderDir}
      />
    </div>
  );
};

export default LessonPage;
